import * as React from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { Box, Grid, Skeleton } from "@mui/material";
import Slider from "../genericComponents/slider/Slider";
import StoreItem from "./StoreItem";
import { StoreCartButton } from "./StoreCartButton";
import { ApiService } from "@/app/services/AppService";
import { ResponseObject } from "@/app/classes/main";
import { getToken } from "@/app/services/HolderService";
import "./index.css";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ProductCartDetail {
  product: {
    id: number;
  };
  quantity: number;
  totalCoins: number;
}
interface CoinDetails {
  productCartDetails: ProductCartDetail[];
  totalCoins: number;
}

interface updatedProductCartDetails {}

interface StoreCategoriesProps {
  open: any;
  close: any;
  subCategoriesData: any;
  setSubCategoriesData: any;
}
const StoreCategories: React.FC<StoreCategoriesProps> = ({
  open,
  close,
  subCategoriesData,
  setSubCategoriesData,
}) => {
  const apiService = React.useMemo(() => new ApiService(), []);
  const [subCategorieProductsData, setSubCategorieProductsData] =
    React.useState([]);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [conisDetails, setConisDetails] = React.useState<CoinDetails>();
  const [reloadProductCart, setReloadProductCart] = React.useState(false);
  const token = getToken();

  const getSubCategorieProducts = React.useCallback(
    async (cid: any, scid: any) => {
      if (cid && scid) {
        try {
          const responseObject = await apiService.get<ResponseObject>(
            apiService.storeSubCategoriesProducts +
              "cid=" +
              cid +
              "&scid=" +
              scid,
            getToken()!
          );
          setSubCategorieProductsData(responseObject?.object);
        } catch (error) {
          console.log("Error :", error);
        }
      }
    },
    [apiService]
  );

  const handleSubCategorieProducts = (data: any, index: any) => {
    const cid = data?.catId;
    const scid = data?.id;
    setSelectedIndex(index);
    setSubCategorieProductsData([]);
    getSubCategorieProducts(cid, scid);
  };

  React.useEffect(() => {
    const cid = subCategoriesData?.data[0]?.catId;
    const scid = subCategoriesData?.data[0]?.id;
    getSubCategorieProducts(cid, scid);
  }, [subCategoriesData, getSubCategorieProducts]);

  const handleCloseStoreDialog = () => {
    close();
    setSelectedIndex(0);
    setSubCategoriesData();
    setSubCategorieProductsData([]);
  };

  const getCoinsDetails = React.useCallback(async () => {
    const responseObject = await apiService.get<ResponseObject>(
      apiService.storeProductsCart,
      getToken()!
    );
    if (responseObject?.status == "success") {
      setConisDetails(responseObject.object);
      setReloadProductCart(false);
    }
  }, [apiService]);

  React.useEffect(() => {
    getCoinsDetails();
  }, [reloadProductCart, getCoinsDetails]);

  const handleAddToCartProducts = async (item: any, bool: boolean) => {
    try {
      // Check if the item already exists in the `conisDetails` array
      const existingItem = conisDetails?.productCartDetails?.find(
        (detail: any) => detail.product.id === item.id
      );
      let updatedCoinDetails;

      let newQuantity = 1;
      if (existingItem) {
        newQuantity = bool
          ? existingItem.quantity + 1
          : existingItem.quantity - 1;
        if (newQuantity < 1) {
          updatedCoinDetails = conisDetails?.productCartDetails?.filter(
            (detail: any) => detail.product.id !== item.id
          );
        } else {
          updatedCoinDetails = conisDetails?.productCartDetails?.map(
            (detail: any) =>
              detail.product.id === item.id
                ? { ...detail, quantity: newQuantity }
                : detail
          );
        }
      } else {
        const newProduct = {
          product: { id: item.id },
          quantity: 1,
        };

        updatedCoinDetails = [
          ...(conisDetails?.productCartDetails || []),
          newProduct,
        ];
      }
      const payload = {
        cid: item.cid,
        productCartDetails: updatedCoinDetails?.map((detail: any) => ({
          pid: detail.product.id,
          quantity: detail.quantity,
        })),
      };

      await apiService.post<ResponseObject>(
        apiService.storeAddToCartProducts,
        payload,
        getToken()!
      );
      setReloadProductCart(true);
    } catch (error) {
      console.log("Error:", error);
    }
  };

  return (
    <React.Fragment>
      <Dialog
        fullScreen
        open={open}
        onClose={handleCloseStoreDialog}
        TransitionComponent={Transition}
      >
        <Box
          sx={{
            width: "-webkit-fill-available",
            textAlign: "end",
            padding: "0.5rem 0.5rem 0px 0px",
            position: "absolute",
            zIndex: 2,
          }}
        >
          <IconButton onClick={handleCloseStoreDialog}>
            <CloseIcon sx={{ cursor: "pointer", color: "white" }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            background: "linear-gradient(135deg, #123524 0%, #1f7a4d 55%, #1a6b42 100%)",
            boxShadow: "0 8px 32px rgba(18, 53, 36, 0.25)",
          }}
        >
          <Box
            sx={{
              width: "100%",
              textAlign: "center",
              color: "white",
              margin: "1rem auto",
              pt: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", sm: "1.25rem" },
                fontWeight: 600,
                letterSpacing: "0.02em",
              }}
            >
              {subCategoriesData?.name}
            </Typography>
          </Box>
          <Box
            className="store-main-slider-bar-salon-at-home"
            sx={{
              overflowX: "scroll",
              textWrap: "nowrap",
              display: "flex",
              alignItems: "center",
              margin: "0rem auto",
              pb: 0.5,
            }}
          >
            {subCategoriesData &&
            subCategoriesData.data &&
            subCategoriesData.data.length > 0
              ? subCategoriesData?.data?.map((item: any, index: any) => (
                  <Box
                    key={index}
                    sx={{
                      borderBottom:
                        selectedIndex === index
                          ? "4px solid #dff6e8"
                          : "4px solid transparent",
                    }}
                    onClick={() => handleSubCategorieProducts(item, index)}
                  >
                    <Slider
                      url={item?.profileImage?.secureHttpPath}
                      title={item?.name}
                    />
                  </Box>
                ))
              : Array.from({ length: 10 }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      margin: "1rem auto",
                    }}
                  >
                    <Skeleton
                      variant="rounded"
                      sx={{
                        margin: "auto",
                        width: "50px",
                        height: "50px",
                        borderRadius: "10rem",
                      }}
                    />
                    <Skeleton
                      key={i}
                      variant="rounded"
                      sx={{
                        margin: "5px",
                        width: "100px",
                        height: "10px",
                        borderRadius: "10rem",
                      }}
                    />
                  </Box>
                ))}
          </Box>
        </Box>
        <Grid
          container
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          sx={{
            background:
              "linear-gradient(180deg, rgba(245, 251, 247, 0.95) 0%, #ffffff 100%)",
            padding: {
              xs: "8px 8px 5.5rem",
              sm: "1rem 1rem 4.5rem",
              md: "2rem 1rem 4.5rem",
            },
            display: "flex",
            margin: "auto",
          }}
        >
          {" "}
          {subCategorieProductsData && subCategorieProductsData.length > 0
            ? subCategorieProductsData?.map((item, index) => (
                <Grid container item xs={6} sm={6} md={4} lg={3} key={index}>
                  <StoreItem
                    item={item}
                    conisDetails={conisDetails}
                    handleAddToCartProducts={handleAddToCartProducts}
                  />
                </Grid>
              ))
            : Array.from({ length: 4 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  sx={{
                    margin: { xs: "0.5rem", sm: "1rem" },
                    width: { xs: "175px", sm: "300px", md: "330px" },
                    height: { xs: "280px", sm: "320px", md: "320px" },
                  }}
                />
              ))}
        </Grid>
        {token && conisDetails && conisDetails?.totalCoins > 0 && (
          <StoreCartButton conisDetails={conisDetails} />
        )}
      </Dialog>
    </React.Fragment>
  );
};

export default StoreCategories;
